import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, isNetworkError } from "@/lib/api";

const AuthCtx = createContext(null);

const DEFAULT_PREFS = { distance_unit: "mi" };

// Last known profile, so the app stays usable offline (session cookie is HttpOnly
// and untouched — this is display data only, cleared on logout).
const USER_CACHE_KEY = "pins:last-user";

function cacheUser(data) {
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data));
  } catch (e) { void e; }
}

function readCachedUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_CACHE_KEY) || "null");
  } catch (e) {
    void e;
    return null;
  }
}

function withDefaults(user) {
  if (!user) return user;
  return { ...user, preferences: { ...DEFAULT_PREFS, ...(user.preferences || {}) } };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(withDefaults(data));
      cacheUser(data);
    } catch (e) {
      if (isNetworkError(e)) {
        // Offline: keep the last known session alive so drafts can be written
        const cached = readCachedUser();
        if (cached) {
          setUser(withDefaults(cached));
          return;
        }
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session_id first,
    // so skip the /me probe (it would 401 before the cookie exists).
    if (window.location.hash?.includes("session_id=")) return;
    check();
  }, [check]);

  const googleSession = useCallback(async (sessionId) => {
    setError("");
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      const { is_new, ...profile } = data;
      setUser(withDefaults(profile));
      cacheUser(profile);
      return { ok: true, isNew: !!is_new, user: profile };
    } catch (e) {
      setUser(false);
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(withDefaults(data));
      cacheUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const register = useCallback(async (name, email, password, acceptedTerms = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password, accepted_terms: acceptedTerms });
      setUser(withDefaults(data));
      cacheUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { void e; }
    try {
      localStorage.removeItem(USER_CACHE_KEY);
    } catch (e) { void e; }
    setUser(false);
  }, []);

  const updatePreferences = useCallback(async (patch) => {
    // Optimistic update
    setUser((u) => (u ? { ...u, preferences: { ...u.preferences, ...patch } } : u));
    try {
      const { data } = await api.patch("/auth/preferences", patch);
      setUser(withDefaults(data));
    } catch (e) { void e; }
  }, []);

  const value = useMemo(
    () => ({ user, error, setError, login, register, googleSession, logout, updatePreferences, refresh: check }),
    [user, error, login, register, googleSession, logout, updatePreferences, check]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
